'use strict'
import * as React from 'react'
import RefreshIcon from '@mui/icons-material/Refresh'
import { useJudgmentRuleSettingDispatch, useJudgmentRuleSettingSelector } from '../extra/store'
import { EMPTY_RESULT_LABEL } from '../extra/classificationField'
import { ChrofyCustomSelect } from '../../../common/ChrofyCustomSelect'
import CustomTooltip from '../../../../_components/CustomTooltip'

/** 判定結果欄の横幅 */
const RESULT_FIELD_WIDTH_CLASS = 'w-[150px]'

/**
 * 判定結果プルダウンを、選択済みラベルが見切れているときだけ 共通ツールチップ（青文字・白背景）で全文表示するラッパー。 ChrofyCustomSelect 内の選択済みテキスト（.ui-select__text）の見切れを計測する。
 *
 * @param {Object} props
 * @param {string} props.title 全文
 * @param {React.ReactNode} props.children ChrofyCustomSelect
 */
const ResultSelectTooltip = ({ title, children }) => {
  const [open, setOpen] = React.useState(false)
  const wrapRef = React.useRef(null)
  const close = React.useCallback(() => setOpen(false), [])

  React.useEffect(() => {
    setOpen(false)
    // ChrofyCustomSelect が付与するネイティブ title を除去し、共通ツールチップと二重表示にならないようにする
    const textEl = wrapRef.current?.querySelector('.ui-select__text')
    if (textEl) textEl.removeAttribute('title')
  }, [title])

  return (
    <CustomTooltip title={title} forceShow open={open} onClose={close} placement="bottom-start">
      <div
        ref={wrapRef}
        className={`inline-block ${RESULT_FIELD_WIDTH_CLASS} min-w-0 align-top`}
        onMouseEnter={() => {
          const textEl = wrapRef.current?.querySelector('.ui-select__text')
          // 実際に見切れている場合のみ表示（微小な誤差を許容）
          if (textEl && textEl.scrollWidth - textEl.clientWidth > 1) setOpen(true)
        }}
        onMouseLeave={close}
        onMouseDown={close}
      >
        {children}
      </div>
    </CustomTooltip>
  )
}

// 判定結果テキスト入力（区分値以外）のスタイル
const RESULT_TEXT_INPUT_CLASS_NAME =
  'w-full h-full min-w-0 p-3 border border-gray-300 rounded text-sm text-center truncate bg-white'

/**
 * 判定結果のプルダウン（区分値選択）。他プルダウンと同じ ChrofyCustomSelect に統一し、 開閉で回転する矢印・長い値の truncate＋ツールチップ（title）を共通化する。
 *
 * @param {Object} props
 * @param {string} props.value 現在の値
 * @param {(e: { target: { value: string } }) => void} props.onChange
 * @param {boolean} props.disabled
 * @param {string[]} props.options 選択肢（区分値の配列）
 * @param {boolean} props.hasEmptyOption 「(空)」選択肢を持つか
 */
const ResultSelectField = ({ value, onChange, disabled, options, hasEmptyOption }) => {
  const selectOptions = [
    ...(hasEmptyOption ? [{ label: EMPTY_RESULT_LABEL, value: '' }] : []),
    ...options.map((option) => ({ label: option, value: option }))
  ]

  const displayLabel = value || (hasEmptyOption ? EMPTY_RESULT_LABEL : '')

  return (
    <ResultSelectTooltip title={displayLabel}>
      <ChrofyCustomSelect
        // 判定結果欄は他プルダウンと異なり、選択済みの値を中央寄せで表示する（従来仕様）。
        // 展開リストは共通コンポーネントのまま左寄せ。
        // Tailwind の任意バリアントでは `_` が空白に変換されるため、クラス名の `__` は `\_\_` とエスケープする。
        // テキストを flex-1 で伸ばして text-center で中央寄せ。矢印は justify-center 併用で右端に寄りすぎないようにする。
        className={`w-full min-w-0 [&_.ui-select\\_\\_text]:flex-1 [&_.ui-select\\_\\_text]:text-center [&_.ui-select\\_\\_text]:mr-0`}
        selectClassName="justify-center"
        // 長い区分値でリストが横に伸びて崩れないよう、各項目を truncate（…）で省略表示する。
        dropdownClassName="[&_.ui-select\\_\\_item]:truncate"
        value={value}
        onChange={onChange}
        disabled={disabled}
        hasNoSelect={false}
        placeholder={hasEmptyOption ? EMPTY_RESULT_LABEL : ''}
        options={selectOptions}
      />
    </ResultSelectTooltip>
  )
}

/**
 * 判定結果のテキスト入力（区分値以外）。長い値は truncate し、ホバーで全文を表示する。
 *
 * @param {Object} props
 * @param {string} props.value 現在の値
 * @param {(e: React.ChangeEvent<HTMLInputElement>) => void} props.onChange
 * @param {boolean} props.disabled
 */
const ResultTextInputField = ({ value, onChange, disabled }) => (
  <div className={`inline-block ${RESULT_FIELD_WIDTH_CLASS} min-w-0 align-top`}>
    <input
      type="text"
      className={RESULT_TEXT_INPUT_CLASS_NAME}
      value={value}
      onChange={onChange}
      placeholder="(空)"
      disabled={disabled}
      title={value || EMPTY_RESULT_LABEL}
    />
  </div>
)

/**
 * 判定ルールリストコンポーネント
 *
 * @param {Object} props
 * @param {boolean} props.isReadOnly 編集不可フラグ
 * @param {boolean} [props.isRuleInputDisabled] 区分項目未設定などで条件式・判定結果の入力を無効化
 * @param {boolean} props.hasJudgmentRulesChanges 判定ルール（IF条件・判定結果・ELSE）に変更あり（リセットボタンのみ活性化）
 * @param {() => void} props.onResetJudgmentRules 判定ルールを初期状態にリセットするコールバック
 */
const RuleList = ({
  isReadOnly = false,
  isRuleInputDisabled = false,
  hasJudgmentRulesChanges = false,
  onResetJudgmentRules
}) => {
  const dispatch = useJudgmentRuleSettingDispatch()
  const inputDisabled = isReadOnly || isRuleInputDisabled
  const { judgmentRules, elseResult, selectedConditionIndex, resultDropdownOptions, resultDropdownHasEmptyOption } =
    useJudgmentRuleSettingSelector((state) => ({
      judgmentRules: state.judgmentRules,
      elseResult: state.elseResult,
      selectedConditionIndex: state.selectedConditionIndex,
      resultDropdownOptions: state.resultDropdownOptions || [],
      resultDropdownHasEmptyOption: state.resultDropdownHasEmptyOption
    }))
  const renderResultSelect = (value, onChange) => (
    <ResultSelectField
      value={value}
      onChange={onChange}
      disabled={inputDisabled}
      options={resultDropdownOptions}
      hasEmptyOption={resultDropdownHasEmptyOption}
    />
  )

  const renderResultTextInput = (value, onChange) => (
    <ResultTextInputField value={value} onChange={onChange} disabled={inputDisabled} />
  )

  const handleAddRule = () => {
    dispatch('ADD_RULE')
  }

  const handleDeleteRule = (index) => {
    dispatch('DELETE_RULE', index)
  }

  const handleSelectCondition = (index) => {
    dispatch('SELECT_CONDITION', index)
  }

  const handleConditionChange = (index, condition) => {
    dispatch('UPDATE_RULE_CONDITION', { index, condition })
  }

  const handleResultChange = (index, result) => {
    dispatch('UPDATE_RULE_RESULT', { index, result })
  }

  const handleElseResultChange = (result) => {
    dispatch('UPDATE_ELSE_RESULT', result)
  }

  // HTMLエスケープ
  const escapeHtml = (text) => {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  // 条件式を要素単位でパースしてReact要素に変換
  const parseConditionExpression = (condition, ruleIndex) => {
    if (!condition) {
      return null
    }

    const tokenClassName = inputDisabled
      ? 'inline-block px-1.5 py-0.5 mx-0.5 rounded bg-[#f3f4f6]'
      : 'inline-block px-1.5 py-0.5 mx-0.5 rounded cursor-pointer bg-[#f3f4f6] hover:bg-[#fee2e2] transition-all'

    const handleTokenClick = (e) => {
      if (inputDisabled) return
      e.stopPropagation()
      handleSelectCondition(ruleIndex)
    }

    // 条件式を要素単位で分割（[今年度.項目名], RANK(), (空), 演算子, 数値（マイナス含む）など）
    const pattern = /(RANK\([^)]+\)|\[[^\]]+\]|\(空\)|>=|<=|==|!=|>|<|\+|\*|\/|AND|OR|\(|\)|\-?\d+\.?\d*|-|"[^"]*")/g
    const matches = []
    let match
    let lastIndex = 0

    while ((match = pattern.exec(condition)) !== null) {
      // マッチの前の空白や文字を追加
      if (match.index > lastIndex) {
        const beforeText = condition.substring(lastIndex, match.index)
        if (beforeText.trim()) {
          matches.push({
            text: beforeText,
            index: lastIndex,
            isSeparator: true
          })
        }
      }
      matches.push({
        text: match[0],
        index: match.index,
        isSeparator: false
      })
      lastIndex = match.index + match[0].length
    }

    // 残りの文字を追加
    if (lastIndex < condition.length) {
      const afterText = condition.substring(lastIndex)
      if (afterText.trim()) {
        matches.push({
          text: afterText,
          index: lastIndex,
          isSeparator: true
        })
      }
    }

    if (matches.length === 0) {
      return (
        <span className={tokenClassName} onClick={handleTokenClick}>
          {condition}
        </span>
      )
    }

    return (
      <>
        {matches.map((matchObj, idx) => {
          if (matchObj.isSeparator) {
            return (
              <span key={idx} className="inline-block px-0.5" style={{ background: 'transparent' }}>
                {matchObj.text}
              </span>
            )
          } else {
            return (
              <span key={idx} className={tokenClassName} onClick={handleTokenClick}>
                {matchObj.text}
              </span>
            )
          }
        })}
      </>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-sm mb-5 border border-gray-300">
      <div className="px-6 py-4 font-semibold flex items-center gap-2 text-base bg-[#e5e7eb] text-[#1f2937] rounded-t-lg">
        ⚡ 判定ルール（上から順に評価）
      </div>
      <div className="p-6">
        <div id="ruleList">
          {judgmentRules.map((rule, index) => (
            <div
              key={index}
              className="flex items-center gap-2.5 p-3.5 border-2 border-gray-200 rounded-lg bg-white mb-2.5 hover:border-gray-300"
            >
              <span className="bg-[#3150A5] text-white px-3 py-1 rounded text-[13px] font-bold">{index + 1}</span>
              <span className="text-gray-600 text-[13px] font-semibold">IF</span>
              <div
                className={`flex-1 p-3 border-2 rounded text-sm transition-all min-h-[46px] flex items-center ${
                  rule.condition ? 'bg-white font-mono' : 'bg-gray-50'
                } ${
                  selectedConditionIndex === index
                    ? 'border-blue1 bg-blue-50 shadow-[0_0_0_3px_rgba(49,80,165,0.15)]'
                    : 'border-gray-300 hover:border-gray-400'
                } ${inputDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                onClick={() => !inputDisabled && handleSelectCondition(index)}
                data-placeholder="クリックして条件を設定..."
              >
                {rule.condition ? (
                  parseConditionExpression(rule.condition, index)
                ) : (
                  <span className="text-gray-400">クリックして条件を設定...</span>
                )}
              </div>
              <span className="text-blue1 font-bold text-lg">→</span>
              {resultDropdownOptions.length > 0
                ? renderResultSelect(rule.result, (e) => handleResultChange(index, e.target.value))
                : renderResultTextInput(rule.result, (e) => handleResultChange(index, e.target.value))}
              {!isReadOnly && (
                <button
                  className="bg-transparent border-none text-gray-400 text-xl px-2 py-1 cursor-pointer hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => handleDeleteRule(index)}
                  disabled={judgmentRules.length <= 1}
                >
                  ×
                </button>
              )}
            </div>
          ))}

          <div className="flex items-center gap-2.5 p-3.5 border-2 border-yellow-400 rounded-lg bg-yellow-50 mb-2.5">
            <span className="bg-yellow-600 text-white px-3 py-1 rounded text-xs font-bold">∞</span>
            <span className="text-yellow-800 text-sm flex-1 font-medium">ELSE（上記以外すべて）</span>
            <span className="text-blue1 font-bold text-lg">→</span>
            {resultDropdownOptions.length > 0
              ? renderResultSelect(elseResult, (e) => handleElseResultChange(e.target.value))
              : renderResultTextInput(elseResult, (e) => handleElseResultChange(e.target.value))}
            <span className="w-9"></span>
          </div>
        </div>

        <div className="flex justify-between items-center mt-4">
          <button
            className="bg-green-500 text-white px-5 py-2.5 rounded text-sm font-medium cursor-pointer hover:bg-green-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleAddRule}
            disabled={isReadOnly}
          >
            + ルールを追加
          </button>
          <button
            type="button"
            onClick={onResetJudgmentRules}
            className="flex items-center justify-center gap-1 px-2 py-1 text-sm text-gray-600 bg-white border border-gray-300 rounded hover:text-gray-800 hover:bg-gray-50 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
            title="リセット"
            disabled={!hasJudgmentRulesChanges || isReadOnly}
          >
            <RefreshIcon className="w-4 h-4" />
            <span>リセット</span>
          </button>
        </div>
      </div>
    </div>
  )
}

export default RuleList
